import type { StatId } from '../domain/stats.ts';
import type { Roller } from './dice.ts';
import type { D20Roll, RollMode } from './rollModes.ts';
import { rollD20 } from './rollModes.ts';

/**
 * A single named contribution to a Check total. Every number the player sees in
 * the breakdown comes from one of these, so the math is never hidden.
 */
export interface CheckComponent {
  label: string;
  value: number;
}

export type CheckKind = 'skill' | 'stat' | 'evade';

export interface CheckResult {
  kind: CheckKind;
  /** Human label, e.g. "Perception" or "Dexterity Check". */
  label: string;
  die: D20Roll;
  components: CheckComponent[];
  total: number;
  /** True when the kept die face is 20 / 1 before modifiers. Rules p.15. */
  naturalTwenty: boolean;
  naturalOne: boolean;
  /** Set when the Skill was attempted with no Ranks. RULE DCC-CHK-003. */
  untrained: boolean;
  untrainedSkillId?: string;
}

function sumComponents(die: D20Roll, components: readonly CheckComponent[]): number {
  return components.reduce((acc, c) => acc + c.value, die.kept);
}

export interface SkillCheckInput {
  skillId: string;
  skillName: string;
  /** The Skill's associated Stat. Null is not permitted: Passive Skills roll nothing. */
  stat: StatId;
  statAbbr: string;
  /** Rank the crawler has in the Skill. 0 means untrained. */
  rank: number;
  /** Stat Mod taken from the ENHANCED Stat value. */
  statMod: number;
  mode: RollMode;
  /** Additional situational modifiers supplied by the player or GM. */
  extraComponents?: readonly CheckComponent[];
}

/**
 * RULE DCC-CHK-001 - Skill Check = 1d20 + Skill Rank + associated Stat Mod.
 * Source: Crawlers & Customization p.20; Rules for Survival p.12.
 *
 * RULE DCC-CHK-003 - Untrained Attack or Utility Skill Checks are rolled with
 * Disadvantage, add the Stat Mod as usual, and add no Ranks.
 * Source: Rules for Survival p.12.
 */
export function resolveSkillCheck(roller: Roller, input: SkillCheckInput): CheckResult {
  if (!Number.isInteger(input.rank) || input.rank < 0) {
    throw new Error(`Skill Rank must be a non-negative integer, received ${input.rank}`);
  }
  const untrained = input.rank === 0;
  const mode: RollMode = untrained ? 'disadvantage' : input.mode;
  const die = rollD20(roller, mode);

  const components: CheckComponent[] = [];
  if (!untrained) {
    components.push({ label: `${input.skillName} Rank`, value: input.rank });
  }
  components.push({ label: `${input.statAbbr} Mod`, value: input.statMod });
  for (const extra of input.extraComponents ?? []) components.push(extra);

  return {
    kind: 'skill',
    label: input.skillName,
    die,
    components,
    total: sumComponents(die, components),
    naturalTwenty: die.kept === 20,
    naturalOne: die.kept === 1,
    untrained,
    untrainedSkillId: untrained ? input.skillId : undefined,
  };
}

export interface StatCheckInput {
  stat: StatId;
  statLabel: string;
  statAbbr: string;
  statMod: number;
  mode: RollMode;
  extraComponents?: readonly CheckComponent[];
}

/**
 * RULE DCC-CHK-002 - A Stat Check works exactly like a Skill Check except that
 * the Skill step is skipped, so no Skill Rank is added.
 * Source: Rules for Survival pp.12-13.
 */
export function resolveStatCheck(roller: Roller, input: StatCheckInput): CheckResult {
  const die = rollD20(roller, input.mode);
  const components: CheckComponent[] = [{ label: `${input.statAbbr} Mod`, value: input.statMod }];
  for (const extra of input.extraComponents ?? []) components.push(extra);

  return {
    kind: 'stat',
    label: `${input.statLabel} Check`,
    die,
    components,
    total: sumComponents(die, components),
    naturalTwenty: die.kept === 20,
    naturalOne: die.kept === 1,
    untrained: false,
  };
}
